import { getLtcPool } from '@shared/calculations/funding/projection/projectionTotals';
import {
  BalanceWithDeltaCell,
  BenefitAmountCell,
  CostCell,
  LtcPoolWithDeltaCell,
  PaidCell,
  WhenCell,
} from './cells';
import { currency, monthDisplay } from './formatters';
import type { StrategyPresentation } from './strategies/types';
import type { ColumnGroup } from './types';

const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);
const avg = (xs: number[]) => (xs.length === 0 ? 0 : sum(xs) / xs.length);

const DEFAULT_POOL_TOOLTIP =
  'End-of-month LTC pool balance, with this month’s change beneath.';

const DEFAULT_PAYOUT_COLUMN_TOOLTIP =
  'Policy payout this month, with cumulative dollars paid by the policy through this month in parentheses.';

/** Column set for the post-payout "After benefits begin" table. */
export function buildPayoutColumnGroups(opts: {
  terms: ClaimTerms;
  yearSnapshots: YearSnapshot[];
  presentation: StrategyPresentation;
}): ColumnGroup[] {
  const { yearSnapshots, terms, presentation } = opts;
  const snapshotByYear = new Map(yearSnapshots.map(s => [s.policyYear, s]));

  const universalGroups: ColumnGroup[] = [
    {
      columns: [
        {
          key: 'when',
          label: 'When',
          tooltip: 'Projection month, policy year, and client age.',
          sticky: true,
          render: ({ month }) => <WhenCell month={month} />,
          renderAggregate: ({ months, label }) => {
            const start = monthDisplay(months[0].context.monthIndex);
            const end = monthDisplay(months[months.length - 1].context.monthIndex);
            const range = start === end ? `Month ${start}` : `Months ${start}–${end}`;
            return (
              <div className="leading-tight">
                <div className="text-sm font-semibold text-gray-900">{label}</div>
                <div className="text-[10px] text-gray-500">
                  {range} · {months.length} mo
                </div>
              </div>
            );
          },
        },
      ],
    },
    {
      columns: [
        {
          key: 'cost',
          label: 'Projected cost',
          tooltip:
            'Projected total monthly care cost (with inflated per-day rate beneath). Based on the inflated phase cost from the care plan.',
          render: ({ month }) => <CostCell month={month} terms={terms} />,
          renderAggregate: ({ months, kind }) => {
            const values = months.map(m => m.facts.effectiveCareCost);
            const isTotal = kind === 'total';
            return (
              <div className="leading-tight">
                <div className="font-medium text-gray-900">
                  {currency(isTotal ? sum(values) : avg(values))}
                </div>
                <div className="text-[10px] text-gray-500">
                  {isTotal ? 'total' : 'avg/mo'}
                </div>
              </div>
            );
          },
        },
        {
          key: 'benefit-amount',
          label: 'Benefit amount',
          tooltip:
            'Policy monthly benefit maximum for this policy year, with the daily equivalent beneath.',
          render: ({ month }) => {
            const snap = snapshotByYear.get(month.context.policyYear);
            if (!snap) return <span className="text-gray-300">—</span>;
            return <BenefitAmountCell snapshot={snap} terms={terms} presentation={presentation} />;
          },
          renderAggregate: ({ months }) => {
            const values = months
              .map(m => snapshotByYear.get(m.context.policyYear)?.monthlyBenefitCap)
              .filter((v): v is number => v !== undefined);
            if (values.length === 0) return <span className="text-gray-300">—</span>;
            return (
              <div className="leading-tight">
                <div className="font-medium text-gray-900">{currency(avg(values))}</div>
                <div className="text-[10px] text-gray-500">avg/mo</div>
              </div>
            );
          },
        },
      ],
    },
    {
      columns: [
        {
          key: 'payout',
          label: 'Projected payout (to date)',
          tooltip: presentation.copy?.payoutColumnTooltip ?? DEFAULT_PAYOUT_COLUMN_TOOLTIP,
          render: ({ month, cumulativeLtcPayoutToDate }) => (
            <PaidCell
              month={month}
              cumulativeToDate={cumulativeLtcPayoutToDate}
              presentation={presentation}
              terms={terms}
            />
          ),
          renderAggregate: ({ cumulativeLtcPayoutAtEnd }) => (
            <div className="leading-tight">
              <div className="font-medium text-gray-900">
                {currency(cumulativeLtcPayoutAtEnd)}
              </div>
              <div className="text-[10px] text-gray-500">paid to date</div>
            </div>
          ),
        },
      ],
    },
    {
      columns: [
        {
          key: 'pool',
          label: 'Pool remaining',
          tooltip: presentation.copy?.poolTooltip ?? DEFAULT_POOL_TOOLTIP,
          render: ({ month, openState }) => (
            <LtcPoolWithDeltaCell
              openState={openState}
              closeState={month.state}
              tooltipContent={
                presentation.poolCellTooltip?.({ month, openState, terms }) ?? undefined
              }
            />
          ),
          renderAggregate: ({ openStateAtStart, closeStateAtEnd, kind }) => (
            <BalanceWithDeltaCell
              opening={
                kind === 'total' ? getLtcPool(closeStateAtEnd) : getLtcPool(openStateAtStart)
              }
              closing={getLtcPool(closeStateAtEnd)}
            />
          ),
        },
        {
          key: 'death-benefit',
          label: 'Death benefit balance',
          tooltip:
            'End-of-month death benefit, with this month’s change beneath. Closes equal to the next month’s opening.',
          render: ({ month, openState }) => (
            <BalanceWithDeltaCell
              opening={openState.pools.deathBenefit}
              closing={month.state.pools.deathBenefit}
              tooltipContent={
                presentation.deathBenefitCellTooltip?.({ month, openState, terms }) ??
                undefined
              }
            />
          ),
          renderAggregate: ({ openStateAtStart, closeStateAtEnd, kind }) => (
            <BalanceWithDeltaCell
              opening={
                kind === 'total'
                  ? closeStateAtEnd.pools.deathBenefit
                  : openStateAtStart.pools.deathBenefit
              }
              closing={closeStateAtEnd.pools.deathBenefit}
            />
          ),
        },
      ],
    },
  ];

  const extras = presentation.extraPayoutColumnGroups?.({ terms, yearSnapshots }) ?? [];
  return [...universalGroups, ...extras];
}
